import React, { useState } from 'react';

interface JoinUpdatesProps {
    profileName?: string;
    session?: any;
}

const JoinUpdates: React.FC<JoinUpdatesProps> = ({ profileName = '', session }) => {
    const [alreadyJoined, setAlreadyJoined] = useState(false);
    const [joinWaitListMessage, setJoinWaitListMessage] = useState('');
    const [joinWaitlistLoading, setJoinWaitlistLoading] = useState(false);
    const [showWaitlistModal, setShowWaitlistModal] = useState(false);
    const [waitlistModalMessage, setWaitlistModalMessage] = useState('');

    const handleWaitlistSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();

        const form = event.currentTarget;
        const email = (form.elements.namedItem('email') as HTMLInputElement).value;

        const sessionEmail = session?.user?.email;
        if (sessionEmail && email.trim().toLowerCase() !== sessionEmail.trim().toLowerCase()) {
            setWaitlistModalMessage(`This doesn't match your account email. Please use ${sessionEmail} to join updates.`);
            setShowWaitlistModal(true);
            return;
        }

        const trimmed = profileName.trim();
        const spaceIndex = trimmed.indexOf(" ");
        const firstName = spaceIndex === -1 ? trimmed : trimmed.slice(0, spaceIndex);
        const lastName = spaceIndex === -1 ? "" : trimmed.slice(spaceIndex + 1);

        setJoinWaitlistLoading(true);
        try {
            const response = await fetch(`${process.env.REACT_APP_API_URL_JOIN_UPDATE || ''}/api/waitlist`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    firstName,
                    lastName,
                    email,
                    gardenSize: "updates",
                }),
            });

            const data = await response.json();

            if (response.ok) {
                if (data.user && data.user.id) {
                    if (data.isExistingUser) {
                        setJoinWaitListMessage('✉️ You’re already signed up for updates. We’ll be in touch soon.');
                        setAlreadyJoined(true);
                    } else {
                        setJoinWaitListMessage('✉️ Thanks for signing up for updates! We’ll be in touch soon.');
                        setAlreadyJoined(true);
                        form.reset();
                    }
                } else {
                    setWaitlistModalMessage('Something went wrong. Please try again.');
                    setShowWaitlistModal(true);
                }
            } else {
                if (response.status === 400) {
                    setWaitlistModalMessage(data.error || 'Please check your information and try again.');
                } else if (response.status === 500) {
                    setWaitlistModalMessage('Server error. Please try again later.');
                } else {
                    setWaitlistModalMessage('Something went wrong. Please try again.');
                }
                setShowWaitlistModal(true);
            }
        } catch (error) {
            console.error('Error:', error);
            setWaitlistModalMessage('Network error. Please check your connection and try again.');
            setShowWaitlistModal(true);
        } finally {
            setJoinWaitlistLoading(false);
        }
    };

    return (
        <>
            <section id="waitlist" className="w-full bg-gradient-to-b from-green-50 to-green-100 border-t border-gray-100 py-16 text-left">
                <div className="container mx-auto max-w-2xl px-4 text-center">
                    <h2 className="text-3xl font-black text-gray-900 tracking-tight" style={{ fontFamily: 'Comic Sans MS, Comic Sans, cursive' }}>Join to get updates</h2>
                    <form onSubmit={handleWaitlistSubmit} className="mt-8 flex flex-col sm:flex-row gap-3 max-w-md mx-auto">
                        {alreadyJoined ? (
                            <div className="flex-grow px-4 py-3 rounded-xl bg-green-50 border border-green-200 text-green-700 text-center">
                                {joinWaitListMessage}
                            </div>
                        ) : (
                            <>
                                <input
                                    type="email"
                                    name="email"
                                    required
                                    placeholder="Enter your email"
                                    className="flex-grow px-4 py-3 rounded-xl border border-gray-200 text-black bg-white focus:outline-none"
                                />
                                <button
                                    type="submit"
                                    disabled={joinWaitlistLoading}
                                    className="bg-green-600 text-white font-bold px-6 py-3 rounded-xl shadow-sm text-sm"
                                >
                                    {joinWaitlistLoading ? (
                                        <div className="flex items-center justify-center gap-2">
                                            <img src="/icons/spinner.svg" alt="Loading" className="h-5 w-5 animate-spin brightness-0 invert" />
                                            <span>Joining...</span>
                                        </div>
                                    ) : 'Join to Get Updates'}
                                </button>
                            </>
                        )}
                    </form>
                </div>
            </section>

            {showWaitlistModal && (
                <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[110] p-4">
                    <div className="bg-white rounded-2xl p-6 max-w-md w-full text-center shadow-xl">
                        <p className="text-gray-700 font-medium mb-4">{waitlistModalMessage}</p>
                        <button onClick={() => setShowWaitlistModal(false)} className="bg-green-600 text-white font-bold px-6 py-2 rounded-xl text-sm">Close</button>
                    </div>
                </div>
            )}
        </>
    );
};

export default JoinUpdates;